import React, { useContext, useEffect, useState } from 'react';
import 'react-leaflet-fullscreen/dist/styles.css';
import FullscreenControl from 'react-leaflet-fullscreen';
import Lottie from 'react-lottie';
import loaderAnimation from '../../../../assets/animations/loader.json';
import { Icon as LegacyIcon } from '@ant-design/compatible';
import { UserOutlined } from '@ant-design/icons';
import {
  Drawer,
  Descriptions,
  Card,
  Avatar,
  Divider,
  Skeleton,
  Tag,
  Modal,
  Col,
  Row,
  List,
} from 'antd';
import { DrawerProps } from 'antd/lib/drawer';
import {
  withScriptjs,
  withGoogleMap,
  GoogleMap,
  Marker,
} from 'react-google-maps';
import Meta from 'antd/lib/card/Meta';
import { MainContext } from '../../../../contexts/mainContext';
import {
  GetServiceDetailsQuery,
  useGetServiceDetailsQuery,
  // useOnDriverLocationUpdateSubscription,
} from '../../../../graphql/datamodel.gen';
import { getClient } from '../../../../apollo';
import { renderServiceStatus } from './../../utils';
import { ServiceStatus } from '../../types';
import { NetworkStatus } from '@apollo/client';
import CarIcon from '../../../../assets/automobile.svg';
import CheckeredIcon from '../../../../assets/checkered.svg';
import { WithGoogleMapProps } from 'react-google-maps/lib/withGoogleMap';
import * as Leaflet from 'react-leaflet';
import { Icon as LeafletIcon } from 'leaflet';
import L from 'leaflet';
import { DriftMarker } from 'leaflet-drift-marker';
import firebase from 'firebase';
import { Lightbox } from 'react-modal-image';
import {
  CSSTransition,
  Transition,
  TransitionGroup,
} from 'react-transition-group';
import RoutingMachine from '../../../maps/RoutingMachine';
import moment from 'moment';

const TILE_MAP_URL = `https://api.mapbox.com/styles/v1/${process.env.REACT_APP_MAPBOX_USERNAME}/${process.env.REACT_APP_MAPBOX_MAP_ID}/tiles/{z}/{x}/{y}?access_token=${process.env.REACT_APP_MAPBOX_ACCESS_TOKEN}`;

interface IServiceInfoContainerProps {
  serviceId: string;
  drawerIsVisible: boolean;
}

const CarIconLeaflet = new LeafletIcon({
  iconUrl: CarIcon,
  iconSize: [35, 35],
});

const ServiceInfoContainer = (props: IServiceInfoContainerProps) => {
  const context = useContext(MainContext);
  const [driverPicDownloadURL, setDriverPic] = useState();

  const {
    loading: loadingService,
    data: dataService,
    networkStatus,
  } = useGetServiceDetailsQuery({
    client: getClient(),
    notifyOnNetworkStatusChange: true,
    returnPartialData: true,
    fetchPolicy: 'cache-and-network',
    pollInterval: 1000 * 60 * 1,
    variables: {
      id: props.serviceId,
    },
  });

  const service = dataService?.service;

  useEffect(() => {
    if (dataService?.service?.driver?.profilePicURI && !driverPicDownloadURL) {
      try {
        firebase
          .storage()
          .refFromURL(dataService?.service?.driver?.profilePicURI)
          .getDownloadURL()
          .then((url) => {
            setDriverPic(url);
          });
      } catch (e) {
        console.error(e);
      }
    }
  }, [dataService, driverPicDownloadURL]);

  const loading = loadingService && NetworkStatus.poll !== networkStatus;

  const defaultStyle = {
    transition: `all .5s`,
    opacity: 0,
  };

  const transitionStyles = {
    entering: { opacity: 1 },
    entered: { opacity: 0, marginBottom: '-140px' },
  };

  return (
    <>
      <TransitionGroup appear={true} exit={true} enter={true}>
        {
          <Transition
            in={loading}
            className={'loader'}
            timeout={2000}
            appear={true}
            enter={true}
            exit={true}
            mountOnEnter={true}
            unmountOnExit={true}
          >
            {(state) => {
              return (
                <Lottie
                  isStopped={!loading}
                  style={{
                    marginBottom: '-100px',
                    marginTop: '-120px',
                    transition: `all .5s`,
                    ...defaultStyle,
                    ...transitionStyles[state],
                  }}
                  speed={4}
                  options={{
                    loop: true,
                    autoplay: true,
                    animationData: loaderAnimation,
                  }}
                  width={250}
                  height={250}
                />
              );
            }}
          </Transition>
        }
        {service && (
          <Transition in={!!service} className={'loader'} timeout={1000}>
            <div style={{ transition: 'all .5s' }}>
              <ServiceInfoView
                drawerBecameVisible={props.drawerIsVisible}
                isClient={context.isClient || false}
                isAdmin={context.isAdmin || false}
                service={service}
                driverMainPicDownloadURL={driverPicDownloadURL}
              />
            </div>
          </Transition>
        )}
      </TransitionGroup>
    </>
  );
};

const MyMapComponent = withScriptjs(
  withGoogleMap((props: any) => {
    return (
      <GoogleMap
        defaultZoom={15}
        center={{ lat: parseFloat(props.lat), lng: parseFloat(props.lng) }}
        defaultCenter={{
          lat: parseFloat(props.lat),
          lng: parseFloat(props.lng),
        }}
      >
        {(props as any).isMarkerShown && (
          <Marker
            icon={{
              url: CarIcon,
              // @ts-ignore
              scaledSize: { width: 45, height: 45 },
            }}
            position={{
              lat: parseFloat(props.lat),
              lng: parseFloat(props.lng),
            }}
          />
        )}
      </GoogleMap>
    );
  }),
);

interface IServiceViewProps {
  isClient: boolean;
  isAdmin: boolean;
  service: GetServiceDetailsQuery['service'];
  driverMainPicDownloadURL?: string;
  drawerBecameVisible: boolean;
}

interface IServiceViewState {
  avatarModalVisible: boolean;
  mapReady: boolean;
  mapSetup: boolean;
}

class ServiceInfoView extends React.Component<
  IServiceViewProps,
  IServiceViewState
> {
  state = {
    avatarModalVisible: false,
    mapReady: false,
    mapSetup: false,
  };

  private mapRef = React.createRef<L.Map>();

  componentDidMount(): void {
    if (this.props.drawerBecameVisible || this.state.mapReady) {
      // @ts-ignore
      this.mapRef?.current?.leafletElement?.invalidateSize?.();
      const bounds = [];

      const location = this.getLocation();

      if (location) {
        if (location.latitude && location.longitude)
          bounds.push([location.latitude, location.longitude]);
        if (
          this.props.service?.departure?.lat &&
          this.props.service?.departure?.lon
        )
          bounds.push([
            this.props.service?.departure?.lat,
            this.props.service?.departure?.lon,
          ]);
        if (
          this.props.service?.arrival?.lat &&
          this.props.service?.arrival?.lon
        )
          bounds.push([
            this.props.service?.arrival?.lat,
            this.props.service?.arrival?.lon,
          ]);

        //@ts-ignore
        this.mapRef.current?.leafletElement?.fitBounds(bounds);
      }
    }
  }

  componentDidUpdate() {
    if (
      (this.props.drawerBecameVisible || this.state.mapReady) &&
      !this.state.mapSetup
    ) {
      // @ts-ignore
      this.mapRef?.current?.leafletElement?.invalidateSize?.();
      const bounds = [];

      const location = this.getLocation();

      if (location) {
        if (location.latitude && location.longitude)
          bounds.push([location.latitude, location.longitude]);
        if (
          this.props.service?.departure?.lat &&
          this.props.service?.departure?.lon
        )
          bounds.push([
            this.props.service?.departure?.lat,
            this.props.service?.departure?.lon,
          ]);
        if (
          this.props.service?.arrival?.lat &&
          this.props.service?.arrival?.lon
        )
          bounds.push([
            this.props.service?.arrival?.lat,
            this.props.service?.arrival?.lon,
          ]);

        // @ts-ignore
        this.mapRef.current?.leafletElement?.fitBounds(bounds);

        this.setState({
          mapSetup: true,
        });
      }

      this.setState({
        mapSetup: true,
      });
    }
  }

  private getLocation() {
    let location;
    if (
      this.props.service?.driver?.inPrisma &&
      this.props.service?.driver?.type === 'collaborator' &&
      this.props.service?.driver?.location?.latitude &&
      this.props.service?.driver?.location?.longitude
    ) {
      location = this.props.service.driver?.location;
    } else if (
      this.props.service?.vehicle?.location?.latitude &&
      this.props.service?.vehicle?.location?.longitude
    ) {
      location = this.props.service?.vehicle?.location;
    }

    return location;
  }

  render() {
    const location = this.getLocation();

    const thereIsDepartureAndArrivalInfo =
      !!this.props.service?.departure?.lat &&
      !!this.props.service?.departure?.lon &&
      !!this.props.service?.arrival?.lat &&
      !!this.props.service?.arrival?.lon;

    const arrivalDateTime = new Date(this.props.service?.arrival?.date);
    arrivalDateTime.setHours(
      Number(this.props.service?.arrival?.time?.split(':')[0]),
      Number(this.props.service?.arrival?.time?.split(':')[1]),
    );
    const departureDateTime = new Date(this.props.service?.departure?.date);
    departureDateTime.setHours(
      Number(this.props.service?.departure?.time?.split(':')[0]),
      Number(this.props.service?.departure?.time?.split(':')[1]),
    );

    const hoursThreshold = 18000000;
    const today = new Date();
    let isServiceOngoing = false;
    let hasServiceStarted = false;
    let hasServiceFinished = false;
    let isWithinShowLocationThreshold = false;

    const fifteenMinutes = 900000;

    if (today.getTime() < departureDateTime.getTime()) {
      isServiceOngoing = false;
      hasServiceStarted = false;
    }

    if (today.getTime() >= departureDateTime.getTime()) {
      hasServiceStarted = true;
    }

    if (hasServiceStarted && today.getTime() <= arrivalDateTime.getTime()) {
      isServiceOngoing = true;
    }

    if (today.getTime() > arrivalDateTime.getTime()) {
      isServiceOngoing = false;
      hasServiceFinished = true;
      hasServiceStarted = false;
    }

    if (
      (hasServiceStarted && isServiceOngoing) ||
      (hasServiceFinished &&
        today.getTime() - arrivalDateTime.getTime() <= hoursThreshold) ||
      (!hasServiceFinished &&
        departureDateTime.getTime() - today.getTime() <= fifteenMinutes)
    ) {
      isWithinShowLocationThreshold = true;
    }

    const showMap =
      thereIsDepartureAndArrivalInfo ||
      (location && isWithinShowLocationThreshold);

    let emptyText = 'Sin información de localización';

    if (isServiceOngoing && !location) {
      emptyText = 'Sin información de localización';
    }

    if (isWithinShowLocationThreshold && !location) {
      emptyText = 'Sin información de localización';
    }

    if (!hasServiceStarted && !isServiceOngoing) {
      emptyText = 'El servicio aún no ha empezado';
    }

    if (hasServiceFinished && !isWithinShowLocationThreshold) {
      emptyText = 'El servicio ha terminado';
    }

    const generalInfo = (
      <Card
        title="Información general"
        style={{ width: 'auto', height: '100%' }}
      >
        <Descriptions column={2} bordered={false} size={'middle'}>
          <Descriptions.Item span={this.props.isAdmin ? 1 : 2} label="Estado">
            {(() => {
              const { color, text } = renderServiceStatus(
                this.props.service?.status as ServiceStatus,
              );
              return (
                <Tag
                  color={color}
                  key={this.props.service?.status || 'unknown'}
                >
                  {text.toUpperCase()}
                </Tag>
              );
            })()}
          </Descriptions.Item>
          {this.props.isAdmin && (
            <Descriptions.Item label="Cliente">
              {this.props.service?.client?.name}
            </Descriptions.Item>
          )}
          {this.props.isAdmin && this.props.service.budgetReadableId && (
            <Descriptions.Item span={2} label="Presupuesto">
              <Tag>{this.props.service.budgetReadableId}</Tag>
            </Descriptions.Item>
          )}
          {this.props.service?.departure?.date &&
            this.props.service?.departure?.time && (
              <Descriptions.Item span={2} label="Fecha de salida">{`${moment(
                this.props.service?.departure?.date,
              ).format('DD/MM/YYYY')} a las ${
                this.props.service?.departure?.time
              }`}</Descriptions.Item>
            )}
          {this.props.service?.arrival?.date &&
            this.props.service?.arrival?.time && (
              <Descriptions.Item span={2} label="Fecha de llegada">{`${moment(
                this.props.service?.arrival?.date,
              ).format('DD/MM/YYYY')} a las ${
                this.props.service?.arrival?.time
              }`}</Descriptions.Item>
            )}
          {this.props.service?.departure?.address && (
            <Descriptions.Item
              span={2}
              label="Dirección de salida"
            >{`${this.props.service?.departure?.address}`}</Descriptions.Item>
          )}
          {this.props.service?.arrival?.address && (
            <Descriptions.Item
              span={2}
              label="Dirección de llegada"
            >{`${this.props.service?.arrival?.address}`}</Descriptions.Item>
          )}
          {this.props.service?.description && (
            <Descriptions.Item
              span={2}
              label="Descripción"
            >{`${this.props.service?.description}`}</Descriptions.Item>
          )}
        </Descriptions>
      </Card>
    );

    const drivingCompanyCard = (
      <Card title="Compañía colaboradora" style={{ width: 'auto' }}>
        <Meta
          title={this.props.service?.drivingCompany?.name}
          description={`ID: ${this.props.service?.drivingCompany?.id}`}
        />
        <Divider />
        <Descriptions column={1} size="small">
          <Descriptions.Item label="Teléfono">
            {this.props.service?.drivingCompany?.phone}
          </Descriptions.Item>
          <Descriptions.Item label="Email">
            {this.props.service?.drivingCompany?.email}
          </Descriptions.Item>
        </Descriptions>
      </Card>
    );

    const centerLocation =
      location?.latitude && location.longitude
        ? [
            Number.parseFloat(location.latitude),
            Number.parseFloat(location.longitude),
          ]
        : [41.3851, 2.1734];

    const assignedDriverCard = (
      <Card
        title="Conductor"
        style={{ width: 'auto' }}
        cover={
          <div style={{ width: '100%', height: '400px' }}>
            {showMap ? (
              // @ts-ignore
              <Leaflet.Map
                // @ts-ignore
                ref={this.mapRef}
                preferCanvas={false}
                whenReady={() => {
                  // @ts-ignore
                  this.setState({
                    mapReady: true,
                  });
                }}
                center={centerLocation}
                zoom={12}
                animate={true}
                style={{
                  position: 'relative',
                  width: '100%',
                  height: '400px',
                }}
              >
                <Leaflet.TileLayer
                  updateWhenZooming={true}
                  detectRetina={false}
                  maxZoom={19}
                  keepBuffer={16}
                  url={TILE_MAP_URL}
                  attribution='© <a href="https://apps.mapbox.com/feedback/">Mapbox</a> © <a href="http://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                />
                {location?.latitude && location?.longitude && (
                  <DriftMarker
                    position={[
                      Number.parseFloat(location.latitude),
                      Number.parseFloat(location.longitude),
                    ]}
                    icon={CarIconLeaflet}
                    duration={200}
                    keepAtCenter={false}
                  />
                )}
                <FullscreenControl position="topleft" />
                {this.props.service?.departure?.lat &&
                  this.props.service?.departure?.lon &&
                  this.props.service?.arrival?.lat &&
                  this.props.service?.arrival?.lon && (
                    <RoutingMachine
                      // @ts-ignore
                      color="#1890ff"
                      map={this.mapRef}
                      road={[
                        L.latLng(
                          this.props.service.departure.lat,
                          this.props.service.departure.lon,
                        ),
                        L.latLng(
                          this.props.service.arrival.lat,
                          this.props.service.arrival.lon,
                        ),
                      ]}
                    />
                  )}
              </Leaflet.Map>
            ) : (
              // <MyMapComponent
              //   lat={Number.parseFloat(location.latitude)}
              //   lng={Number.parseFloat(location.longitude)}
              //   isMarkerShown={true}
              //   googleMapURL={`https://maps.googleapis.com/maps/api/js?v=3.exp&key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}&libraries=geometry,drawing,places`}
              //   loadingElement={<div style={{ height: `100%` }} />}
              //   containerElement={
              //     <div
              //       style={{
              //         height: '400px',
              //       }}
              //     />
              //   }
              //   mapElement={<div style={{ height: `100%` }} />}
              // />
              <div
                style={{
                  width: '100%',
                  height: '400px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: '#fafafa',
                }}
              >
                <span>{emptyText}</span>
              </div>
            )}
          </div>
        }
      >
        <Meta
          avatar={
            <div
              style={{ cursor: 'pointer' }}
              onClick={() => {
                if (this.props.driverMainPicDownloadURL) {
                  this.setState({
                    avatarModalVisible: true,
                  });
                }
              }}
            >
              <Avatar
                size={100}
                icon={<UserOutlined translate={undefined} />}
                src={this.props.driverMainPicDownloadURL}
              />
            </div>
          }
          title={this.props.service?.driver?.name}
          description={
            <Descriptions column={1} size="small">
              <Descriptions.Item label="Teléfono">
                {(() => {
                  if (this.props.service?.driver) {
                    const phone =
                      this.props.service?.driver?.phone ||
                      this.props.service?.driver?.alternatePhone;

                    return phone;
                  }
                })()}
              </Descriptions.Item>
              <Descriptions.Item label="Email">
                {(() => {
                  if (this.props.service?.driver) {
                    return this.props.service?.driver?.email;
                  }
                })()}
              </Descriptions.Item>
            </Descriptions>
          }
        />

        {this.props.isAdmin && (
          <div style={{ paddingTop: '25px' }}>
            <Descriptions column={1} size="small">
              <Descriptions.Item label="ID">
                {this.props.service?.driver?.id}
              </Descriptions.Item>
              <Descriptions.Item label="Tipo">
                {(() => {
                  if (this.props.service?.driver) {
                    return this.props.service?.driver?.type === 'collaborator'
                      ? 'Colaborador'
                      : 'Propio';
                  }
                })()}
              </Descriptions.Item>
              <Descriptions.Item label="Estado">
                {(() => {
                  if (this.props.service?.driver) {
                    const status = this.props.service?.driver?.status;
                    let color;
                    let localizedStatus = 'Inactivo';
                    if (status === 'active') {
                      color = 'green';
                      localizedStatus = 'Activo';
                    }
                    if (status === 'inactive') {
                      color = 'volcano';
                      localizedStatus = 'Inactivo';
                    }
                    return (
                      <Tag color={color} key={status}>
                        {localizedStatus.toUpperCase()}
                      </Tag>
                    );
                  }
                })()}
              </Descriptions.Item>
            </Descriptions>
          </div>
        )}
      </Card>
    );

    const vehicleCard = (
      <Card title="Vehículo">
        <Meta
          avatar={
            <div className="license license-small es">
              {this.props.service?.vehicle?.licensePlate}
            </div>
          }
          title={(() => {
            return this.props.service?.vehicle?.brand &&
              this.props.service?.vehicle?.model
              ? `${this.props.service?.vehicle?.brand} ${this.props.service?.vehicle?.model}`
              : 'Modelo no disponible';
          })()}
          description={
            this.props.isAdmin &&
            (() => {
              const status = this.props.service?.vehicle?.status;
              if (status === 'active') {
                return (
                  <Tag color={'green'} key={'active'}>
                    {'ACTIVO'}
                  </Tag>
                );
              } else if (status === 'inactive') {
                return (
                  <Tag color={'volcano'} key={'inactive'}>
                    {'INACTIVO'}
                  </Tag>
                );
              } else {
                return 'Informacíon de estado no disponible';
              }
            })()
          }
        />
        {this.props.isAdmin && (
          <div style={{ paddingTop: '25px' }}>
            <Descriptions size="small">
              <Descriptions.Item label="ID">
                {this.props.service?.vehicle?.id}
              </Descriptions.Item>
            </Descriptions>
          </div>
        )}
      </Card>
    );

    const passengers = (
      <Card title={'Pasajeros'}>
        <List
          itemLayout="horizontal"
          dataSource={[...this.props.service.passengers]}
          renderItem={(passenger) => {
            const description = (
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  justifyContent: 'space-between',
                  alignItems: 'baseline',
                }}
              >
                {passenger.email ? (
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'row',
                      justifyContent: 'flex-start',
                      alignItems: 'center',
                    }}
                  >
                    <LegacyIcon
                      type={'mail'}
                      style={{ marginRight: '0.5em' }}
                    />
                    <a
                      style={{ color: 'grey' }}
                      href={`mailTo:${passenger.email}`}
                    >
                      {passenger.email}
                    </a>
                  </div>
                ) : undefined}
                {passenger.phone && (
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'row',
                      justifyContent: 'flex-start',
                      alignItems: 'center',
                    }}
                  >
                    <LegacyIcon
                      type={'phone'}
                      style={{ marginRight: '0.5em' }}
                    />
                    {passenger.phone}
                  </div>
                )}
              </div>
            );

            return (
              <List.Item>
                <List.Item.Meta
                  title={<h3>{passenger.name}</h3>}
                  description={description}
                />
              </List.Item>
            );
          }}
        />
      </Card>
    );

    return (
      <>
        <Row gutter={[24, 24]}>
          <Col
            span={
              this.props.isAdmin && this.props.service?.drivingCompany ? 12 : 24
            }
            offset={0}
          >
            {generalInfo}
          </Col>
          {this.props.isAdmin && this.props.service?.drivingCompany && (
            <Col span={12}>{drivingCompanyCard}</Col>
          )}
        </Row>
        <Divider />
        {/*{generalInfo}*/}
        <Row gutter={[24, 24]}>
          <Col xl={12} lg={24} sm={24} md={24}>
            {this.props.service?.driver && assignedDriverCard}
            {!this.props.service?.driver && 'No hay ningún conductor asignado'}
          </Col>
          <Col xl={12} lg={24} sm={24} md={24}>
            {this.props.service?.vehicle && vehicleCard}
            {!this.props.service?.vehicle && 'No hay ningún vehículo asignado'}
            <Divider />
            {this.props.service?.passengers?.length > 0 && passengers}
            {!this.props.service?.passengers?.length &&
              'No hay pasajeros asignados'}
          </Col>
        </Row>

        {this.state.avatarModalVisible && this.props.driverMainPicDownloadURL && (
          <Lightbox
            hideDownload={true}
            medium={this.props.driverMainPicDownloadURL}
            onClose={() => {
              this.setState({
                avatarModalVisible: false,
              });
            }}
          />
        )}
      </>
    );
  }
}

interface IServiceInfoDrawer extends DrawerProps {
  visible: boolean;
  serviceId?: string;
  onClose: () => void;
}

const ServiceDetails: React.FC<IServiceInfoDrawer> = (
  props: IServiceInfoDrawer,
) => {
  const mainContext = useContext(MainContext);
  const [drawerIsVisible, setDrawerIsVisible] = useState(false);

  const device = mainContext.device;

  let drawerWidth = '50%';

  if (device.type === 'desktop' && !device.bigScreen) {
    drawerWidth = '75%';
  }
  if (device.type === 'desktop' && device.bigScreen) {
    drawerWidth = '40%';
  }

  return (
    <Drawer
      destroyOnClose={false}
      title={`Resumen del servicio ${props.serviceId}`}
      width={drawerWidth}
      visible={props.visible}
      onClose={props.onClose}
      afterVisibleChange={(visible) => {
        setDrawerIsVisible(visible);
      }}
    >
      {props.serviceId && (
        <ServiceInfoContainer
          serviceId={props.serviceId}
          drawerIsVisible={drawerIsVisible}
        />
      )}
    </Drawer>
  );
};

export default ServiceDetails;
